import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { BehaviorSubject, Observable, ReplaySubject, filter, firstValueFrom } from 'rxjs';
import { dataPath, platformPath, adminPath, homePath } from 'src/app/app-routing.module';
import { Tenant } from 'src/app/shared/models/tenant';
import { readFromSessionStorage, writeToSessionStorage } from 'src/app/shared/utils/session-storage.utils';
import { TenantService } from '../../core/services/application/tenant.service';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';

@Injectable({
    providedIn: 'root'
})
export class TenantContextService {
    readonly tenantContextStorageKey = 'CDP_TENANT_CONTEXT';
    private _currentTenant?: Tenant;
    private _showTenantSwitcher = false;
    private _tenants: Tenant[] = [];
    private loggedInUser$!: Observable<AuthState>;

    loadingTenant = false;
    tenantLoadFailed = false;

    get currentTenant(): Tenant | undefined {
        return this._currentTenant;
    }
    get showTenantSwitcher(): boolean {
        return this._showTenantSwitcher;
    }
    public get tenants(): Tenant[] {
        return this._tenants;
    }
    // broadcasts when a tenant has been selected
    public get tenantSelected$(): Observable<Tenant | undefined> {
        return this.tenantSelectedSubject.asObservable();
    }
    private tenantSelectedSubject = new ReplaySubject<Tenant | undefined>(1);

    // broadcasts when a tenant is ready to be selected
    public get tenantContextReady$(): Observable<boolean> {
        return this.tenantContextReady.asObservable();
    }
    private tenantContextReady = new BehaviorSubject<boolean>(false);

    constructor(
        private router: Router,
        private tenantService: TenantService,
        private _oktaAuthStateService: OktaAuthStateService,
        private oktaAuth: OktaAuthService
    ) {
        this.loggedInUser$ = this._oktaAuthStateService.authState$.pipe(
            filter((authState: AuthState) => !!authState && !!authState.isAuthenticated)
        );

        this.loggedInUser$.subscribe(() => {
            this.prepTenantContext();
        });
    }

    public async prepTenantContext(): Promise<boolean> {
        return this.tenantService.getTenants().then(async tenants => {
            this._tenants = tenants;
            const currentTenantCode = readFromSessionStorage(this.tenantContextStorageKey);
            this._currentTenant = this._tenants.find(tenant => tenant.tenantId === currentTenantCode);
            this.tenantContextReady.next(true);
            this.tenantSelectedSubject.next(this._currentTenant);

            await this._handleSingleOrNoTenant();
            return !!this.currentTenant;
        });
    }

    async setTenantContext(tenantId?: string, navigate = true): Promise<void> {
        if (tenantId) {
            return firstValueFrom(this.tenantService.getTenant(tenantId)).then(tenant => {
                this._currentTenant = tenant;
                writeToSessionStorage(this.tenantContextStorageKey, this._currentTenant?.tenantId || null);
                if (navigate) {
                    this.routeAsNeededForTenant(this._currentTenant?.tenantId);
                }
                this.tenantSelectedSubject.next(this._currentTenant);
            });
        } else {
            this._currentTenant = undefined;
            writeToSessionStorage(this.tenantContextStorageKey, null);
            if (navigate) {
                this.routeAsNeededForTenant();
            }
            this.tenantSelectedSubject.next(this._currentTenant);
        }
    }

    clearTenantContext(): void {
        this._currentTenant = undefined;
        writeToSessionStorage(this.tenantContextStorageKey, null);
        this.routeAsNeededForTenant();
    }

    routeAsNeededForTenant(tenantId?: string): void {
        const rootsThatCanBeRedirected = [platformPath, dataPath, homePath, adminPath];
        const urlParts = window.location.pathname.split('/');
        const rootTab = urlParts[1];

        if (rootsThatCanBeRedirected.includes(rootTab) && tenantId) {
            this.routeForTenant(urlParts, tenantId);
        }
    }

    private routeForTenant(urlParts: string[], tenantId?: string) {
        const rootTab = urlParts[1];
        const isSameTenant = urlParts[2] === tenantId;
        const deepSegments = isSameTenant
            ? urlParts
                  .slice(3)
                  .map(s => s.split('?')[0])
                  .filter(Boolean)
            : urlParts[3]
              ? [urlParts[3].split('?')[0]]
              : [];
        this.router.navigate([rootTab, tenantId, ...deepSegments]);
    }

    async refreshTenantSwitcherState(): Promise<void> {
        return this.tenantService.getTenants().then(tenants => {
            this._tenants = tenants;
            this._handleSingleOrNoTenant();
        });
    }

    private _handleSingleOrNoTenant(): Promise<void> | void {
        this._showTenantSwitcher = this._tenants.length > 1;
        if (this._tenants.length > 1) {
            return;
        }

        if (this.tenants.length === 1) {
            const singleAvailableTenant = this._tenants[0];
            if (this._currentTenant?.tenantId !== singleAvailableTenant.tenantId) {
                return this.setTenantContext(singleAvailableTenant.tenantId);
            }
        }

        this._handleNoTenantsAsNeeded();
    }

    private _handleNoTenantsAsNeeded(): Promise<void> | void {
        const adminMonitoringPath = '/global-admin/monitoring-reports';
        if (this.tenants.length === 0 && this.oktaAuth.isGlobalAdmin()) {
            // navigate to the correct admin path if deep linked
            if (window.location.pathname !== adminMonitoringPath) {
                this.router.navigate(['/', 'global-admin']);
            }
        } else if (this.tenants.length === 0) {
            this.router.navigate(['/not-authorized']);
        }
    }

    getTenant() {
        return this._currentTenant;
    }

    async handleParamChange(params: Params): Promise<void> {
        const tenantId = <string>params['id'];
        this.loadingTenant = true;
        this.tenantLoadFailed = false;
        await this.setTenantContext(tenantId, false);
        this.loadingTenant = false;

        if (!tenantId || !this.currentTenant) {
            this.tenantLoadFailed = true;
        } else {
            this.tenantLoadFailed = false;
        }
    }
}
